
import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { nuiteeBookingService } from '@/services/nuiteeBookingService';
import { formatDateToYYYYMMDD } from '@/utils/dateUtils';
import {
  openNuiteeSsoInNewTab,
  type SsoOpenSurface,
} from '@/lib/booking/openNuiteeSsoInNewTab';
import { WebAnalytics } from '@/lib/analytics';
import {
  EVENT_BOOKING_SSO_TAB_OPENED,
  EVENT_BOOKING_SSO_POPUP_BLOCKED,
  EVENT_BOOKING_SSO_API_ERROR,
} from '@/lib/analyticsEvents';

/**
 * Shared hook for opening the Nuitee whitelabel hotel listing in a new tab with SSO.
 * Used by the Results page (Hotels tab "see all hotels" link) and the top nav "Book" link.
 * Default: check-in 30 days from now, 2 nights, 2 adults, 1 room.
 *
 * Uses the tab-first pattern: opens a placeholder tab synchronously on the user-gesture
 * stack, then navigates it to the SSO URL once the API call completes. This prevents
 * popup blockers from silently swallowing the open.
 */
export function useOpenHotelsWhitelabel(): {
  openHotelsWhitelabel: (surface?: SsoOpenSurface) => void;
  isOpening: boolean;
  blockedLoginUrl: string | undefined;
  clearBlockedState: () => void;
} {
  const [isOpening, setIsOpening] = useState(false);
  /** Set when the popup was blocked; used to drive the BlockedModal. */
  const [blockedLoginUrl, setBlockedLoginUrl] = useState<string | undefined>(undefined);

  const openHotelsWhitelabel = useCallback((surface: SsoOpenSurface = 'header') => {
    setIsOpening(true);
    setBlockedLoginUrl(undefined);

    const now = new Date();
    const checkInDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 30);
    const checkOutDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 32);
    const checkIn = formatDateToYYYYMMDD(checkInDate);
    const checkOut = formatDateToYYYYMMDD(checkOutDate);

    openNuiteeSsoInNewTab({
      surface,
      fetchLoginUrl: async () => {
        const deepLinkPath = nuiteeBookingService.buildWhitelabelListingDeepLinkPath({
          checkIn,
          checkOut,
          adults: 2,
          rooms: 1,
        });
        const ssoResponse = await nuiteeBookingService.getSSOLink(deepLinkPath);
        if (!ssoResponse.data?.loginUrl) {
          throw new Error('Missing loginUrl in SSO response');
        }
        return ssoResponse.data.loginUrl;
      },
      onSuccess: (loginUrl) => {
        WebAnalytics.track(EVENT_BOOKING_SSO_TAB_OPENED, {
          surface,
          loginUrl: !!loginUrl,
          ...WebAnalytics.getReferringCreatorProps(),
        });
        setIsOpening(false);
      },
      onBlocked: (loginUrl) => {
        WebAnalytics.track(EVENT_BOOKING_SSO_POPUP_BLOCKED, { surface, hasLoginUrl: !!loginUrl });
        setBlockedLoginUrl(loginUrl);
        setIsOpening(false);
      },
      onApiError: (errorCode, errorStatus) => {
        WebAnalytics.track(EVENT_BOOKING_SSO_API_ERROR, { surface, errorCode, errorStatus });
        if (errorCode === 'USER_NOT_FOUND' || errorCode === 'MISSING_USER_EMAIL') {
          toast.error('Please log in to book hotels.');
        } else if (errorStatus === 401 || errorStatus === 403) {
          toast.error('Please log in to book hotels.');
        } else if (errorStatus === 429) {
          toast.error('Too many requests. Please wait a moment and try again.');
        } else if (errorStatus === 503 || errorStatus === 504) {
          toast.error('Booking service is temporarily unavailable. Please try again later.');
        } else {
          toast.error('Unable to open hotel search. Please try again.');
        }
        setIsOpening(false);
      },
    });
  }, []);

  const clearBlockedState = useCallback(() => {
    setBlockedLoginUrl(undefined);
  }, []);

  return { openHotelsWhitelabel, isOpening, blockedLoginUrl, clearBlockedState };
}
